import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders, HttpInterceptor,
  HttpRequest,
  HttpXsrfTokenExtractor
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  from,
  Observable, of,
  interval as ofInterval,
  throwError
} from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { AppState } from '../app.service';
import { AppSettings } from '../app.settings';
import { LoginSharedService } from '../login/login-shared.service';
import { SessionService } from '../session-maintenance/session.service';
import { ResourceCacheService } from './resource-cache.service';

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
    private static readonly SESSION_CALL_REPEATS: number = 3;

    constructor(private readonly loginShared: LoginSharedService,
        private readonly _cache: ResourceCacheService,
        private readonly appService: AppState,
        private readonly tokenExtractor: HttpXsrfTokenExtractor,
        private readonly sessionService: SessionService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const headerName = 'X-XSRF-TOKEN';
        let token = this.tokenExtractor.getToken() as string;
        let copiedReq;
        if (token !== null && !req.headers.has(headerName)) {
            copiedReq = req.clone({ withCredentials: true, headers: req.headers.set(headerName, token) });
        } else {
            copiedReq = req.clone({ withCredentials: true });
        }
        if (!req.url.startsWith(AppSettings.AUTH_ENDPOINT)) {
            this.sessionService.updateSessionLastAccessedTime();
        }
        return this.request(copiedReq, next);
    }

    request(req: HttpRequest<any>, next: HttpHandler, counter: number = 1): Observable<any> {
        return from(this._cache.get(req.url)).pipe(
            mergeMap(cachedResponse => {
                if (cachedResponse) return of(cachedResponse);

                return next.handle(req).pipe(
                    map((response: any) => {
                        if (response.status === 200) this._cache.save(req.url as string, response);
                        return response;
                    }),
                    catchError(error => {
                        if (error.status === 403 && !req.url.startsWith(AppSettings.AUTH_ENDPOINT)) {
                            if (this.sessionService.isSessionCallActive()) return this.repeatRequest(req, next, counter, 100);

                            if (counter >= SessionInterceptor.SESSION_CALL_REPEATS) {
                                this.loginShared.publishSessionEvent('Clear Cookies');
                                return this.handleError('Cannot reach the service');
                            }

                            counter++;
                            const addAuthorization = counter <= 2;
                            const headers = new HttpHeaders({ 'Expire-Cookies': addAuthorization ? 'None' : 'In-Validate' });
                            return this.appService.refreshToken(headers, addAuthorization).pipe(
                                mergeMap(() => this.repeatRequest(req, next, counter)),
                                catchError(err => {
                                    this.loginShared.publishSessionEvent('Clear Cookies');
                                    return this.handleError(err);
                                })
                            );
                        } else {
                            return this.handleError(error);
                        }
                    })
                );
            })
        );
    }

    private repeatRequest(req: HttpRequest<any>, next: HttpHandler, counter: number = SessionInterceptor.SESSION_CALL_REPEATS, interval: number = 0): Observable<any> {
        return ofInterval(interval).pipe(
            take(1),
            mergeMap(() => {
                if (this.sessionService.isSessionCallActive()) {
                    return this.repeatRequest(req, next, counter, interval);
                }
                return this.request(req, next, counter);
            }),
            catchError(error => {
                if (error.status === 403) return this.request(req, next, counter);
                return this.handleError(error);
            })
        );
    }

    private handleError(error: HttpErrorResponse | string) {
        console.error(error);
        const message = error instanceof HttpErrorResponse
            ? (error.error?.message || 'Server Error')
            : (error || 'Server Error');
        return throwError(() => new Error(message, { cause: error }));
    }
}
